/**
 * Client-side mirror of backend_strapi/utils/imageProcessor.getWatermarkedUrl.
 *
 * Why this exists: the backend persists a `watermarkedImage` file record and
 * SUSU Closet used to read it, falling back to the CLEAN image when it was
 * missing. That record was silently failing to save (the Cloudinary watermark
 * URL is 550-850 chars against a varchar(255) column), so unlicensed users were
 * being served unwatermarked previews — a licence bypass, not a display glitch.
 *
 * The watermark is a pure function of the original Cloudinary URL, so deriving
 * it here means the preview is never clean just because a DB write failed.
 * SHEYOU gets the already-watermarked URL straight from the API; this keeps
 * SUSU Closet fail-closed on the same principle.
 *
 * Keep in sync with backend_strapi/utils/imageProcessor.js.
 */

export function getWatermarkedUrl(originalUrl, designerName) {
  if (!originalUrl) return originalUrl;

  // Only Cloudinary URLs support the transformation.
  if (!originalUrl.includes('res.cloudinary.com')) return originalUrl;

  // Already watermarked — don't double-apply.
  if (originalUrl.includes('l_text:')) return originalUrl;

  const parts = originalUrl.split('/upload/');
  if (parts.length !== 2) return originalUrl;

  const designer = designerName ? String(designerName).toUpperCase().trim() : '';
  const watermarkText = designer
    ? `SHEYOU%20%20x%20%20${designer.replace(/ /g, '%20')}`
    : 'SHEYOU';

  const centerWm = `l_text:Courier%20New_120_bold_letter_spacing_14:${watermarkText},co_white,o_40,a_-25`;
  const tlWm = `l_text:Courier%20New_80_bold_letter_spacing_10:${watermarkText},co_white,o_28,a_-25,g_north_west,x_60,y_180`;
  const brWm = `l_text:Courier%20New_80_bold_letter_spacing_10:${watermarkText},co_white,o_28,a_-25,g_south_east,x_60,y_180`;
  const trWm = `l_text:Courier%20New_60_bold_letter_spacing_8:${watermarkText},co_white,o_22,a_-25,g_north_east,x_50,y_400`;
  const blWm = `l_text:Courier%20New_60_bold_letter_spacing_8:${watermarkText},co_white,o_22,a_-25,g_south_west,x_50,y_400`;

  const watermarkTransformation = `${centerWm}/${tlWm}/${brWm}/${trWm}/${blWm}/`;
  const remainingPath = parts[1].replace(/^\/+/, '');

  return `${parts[0]}/upload/${watermarkTransformation}${remainingPath}`;
}

/**
 * Resolve the image to display when a licence is required.
 * Prefers the stored watermarked file, derives one if it's missing, and only
 * ever returns the clean URL when no watermark can be produced at all.
 */
export function resolveLicensedPreviewUrl(storedWatermarkUrl, cleanUrl, designerName) {
  if (storedWatermarkUrl) return storedWatermarkUrl;
  if (!cleanUrl) return cleanUrl;

  const derived = getWatermarkedUrl(cleanUrl, designerName);
  if (derived && derived !== cleanUrl) return derived;

  // Non-Cloudinary asset: nothing we can overlay client-side.
  console.warn('[watermark] Could not derive a watermarked URL; serving clean image', cleanUrl);
  return cleanUrl;
}
